<template>
    <div id="modal-copy-image">
        <div class="rp-overlay"></div>
        <div class="rp-modal" style="width: 470px;">
            <div class="rp-modal-header">
                <h3 class="rp-modal-title">{{ $t('Copy image') }}</h3>
                <button class="rp-btn-dismiss" @click="$emit('closeModal')">
                    <XIcon class="w-5 h-5" />
                </button>
            </div>
            <div class="bg-white p-5">
                <div v-if="!layout.loading && !layout.success">
                    
                    <div class="mb-4">
                        <div class="text-base font-medium">{{ $t('Make a copy of the image') }}</div> 
                    </div>

                    <div v-if="layout.validate && layout.error" class="text-xs font-medium mt-2 text-red-600">
                        {{ layout.error }}
                    </div>
                    
                    <div class="mb-4">
                        <label class="block text-sm text-gray-700 mb-2">{{ $t('Destination') }}</label>
                        <select class="form-control" v-model="layout.destinationDataset" @change="updateDestinationData">
                            <option :value="false">- {{ $t('Select the destination dataset') }} -</option>
                            <option v-for="(ds) in layout.datasets" :value="ds" :key="ds.id">
                                {{ ds.name }} ({{ ds.createdDate }})
                            </option>
                        </select>
                        
                        <div class="text-sm text-gray-500 mt-1 ml-1" v-if="layout.destinationDataset">
                            {{ layout.destinationDataset.typeName }}
                            <span>/ {{ $t('Created') }}: {{ layout.destinationDataset.createdDate }}</span>
                        </div>
                    </div>
                    
                    <div v-if="layout.destinationDataset?.tagStats && layout.destinationTag">
                        
                        <div class="mb-0">
                            <label class="block text-sm text-gray-700 mb-2">{{ $t('Tag') }}</label>
                            <select class="form-control" v-model="layout.destinationTag">
                                
                                <template v-if="layout.destinationDataset.type == 'MULTICLASS'">
                                    <option v-for="(key, value) in Object.keys(layout?.destinationDataset.tagStats.tags)" :value="key" :key="key">
                                        {{ key === '0' ? 'Unclassified' : key }} ({{ value || 0}})
                                    </option>
                                </template>

                                <template v-else-if="layout.destinationDataset.type=='imageObjectDetection'">
                                    <option value="0">{{ $t('Unclassified')}} ({{ layout?.destinationDataset.tagStats.notLabeled || 0 }})</option>
                                </template>

                                <template v-else-if="layout.destinationDataset.type=='MULTILABEL'">
                                    <option value="0">{{ $t('Anomaly')}} ({{ layout?.destinationDataset.tagStats.anomaly || 0 }})</option>
                                    <option value="OK">{{ $t('Normal')}} ({{ layout?.destinationDataset.tagStats.normal || 0 }})</option>
                                </template>

                            </select>
                        </div>

                        <!-- <template v-if="layout.destinationDataset.type == 'MULTICLASS' && layout.dataset.type=='MULTICLASS'">
                            <div class="mb-4">
                                <label class="block text-sm text-gray-700 mb-2">{{ $t('Labeling') }}</label>
                                <select class="custom-select" v-model="layout.toMasks">
                                    <option :value="false">{{ $t('Not include the mask, only image')}}</option>
                                </select>
                            </div>
                        </template> -->

                    </div>

                    <div class="rp-modal-body-buttons gap-3 mt-4">
                        <button class="rp-btn-secondary" @click="$emit('closeModal')">
                            {{ $t('Cancel') }}
                        </button>
                        <button class="rp-btn-primary" @click="copyImage()"
                            :disabled="!enableCopyButton">
                            {{ $t('Copy') }}
                        </button>
                    </div>

                </div>

                <div v-else-if="layout.loading" class="flex flex-col items-center justify-center py-10"> 
                    <img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20 mb-3" />
                    <LoadingIcon icon="three-dots" class="w-8 h-8 text-primary-500" />
                    <div class="text-gray-600 mt-3">{{ $t('Copying image...') }}</div>
                </div>

                <div v-else-if="layout.success" class="text-center py-10">
                    <div class="flex flex-col items-center">
                        <img :src="require(`@/assets/images/rosepetal/icon/ok.png`)" class="w-20 mb-4">
                        <div class="text-xl text-gray-800 mb-1">{{ $t('Image copied successfully') }}</div>
                        <div class="text-sm text-gray-600">{{ $t('The image has been copied to the selected dataset.') }}</div>
                    </div>
                </div>
                
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import * as rosepetalModel	        from "rosepetal-model";

let $dataset = rosepetalModel.default.dataset;

const props = defineProps({
    imageId: {
        type: String,
        required: true,
    },
    datasets: {
        type: Array,
        required: false,
    }
});

const enableCopyButton = computed(() => {
    if (!layout.value.destinationDataset || !layout.value.destinationTag || layout.value.error) {
        return false;
    }
    return true;
});

const layout = ref({
    imageId:            '',
    datasets:           [],
    loading:            false,
    success:            false,
    error:              false,
    validate:           false,
    destinationDataset: false,
    destinationTag:     false,
});

const updateDestinationData = async () => {
    layout.value.destinationTag = false;
    if (layout.value.destinationDataset) {
        const dsData = await $dataset.get(layout.value.destinationDataset.id, { tagStats: true });
        layout.value.destinationDataset['tags']     = dsData['tags'];
        layout.value.destinationDataset['tagStats'] = dsData['tagStats'];
        layout.value.destinationTag                 = dsData['tags'] ? Object.keys(dsData['tagStats']['tags'])[0] : false;
    }
}

const copyImage = async () => {
    layout.value.error = false;

    if (!layout.value.imageId || !layout.value.destinationDataset || !layout.value.destinationTag) {
        layout.value.error = "Missing required data for image copy.";
        return;
    }

    layout.value.loading = true;

    const images = { [layout.value.imageId]: layout.value.destinationTag };
    try {
        const response = await $dataset.createOperation(layout.value.destinationDataset.id, { images: images, type: 'copy' });
        layout.value.loading = false;

        if (!response.error) {
            layout.value.success = true;
        } else { 
            layout.value.error = response.error; 
        }
    } catch (error) {
        console.error("Copy operation failed:", error);
        layout.value.loading = false;
        layout.value.error = error.message || "Copy failed";
    }
}

onMounted(async () => {
    if (props.datasets) {
        layout.value.datasets = props.datasets;
    }
    if (props.imageId) {
        layout.value.imageId = props.imageId;
    }
});
</script>